import { Box, createStyles, TFCard, TFText } from '@tf/ui';
import { D } from '@tf/utils';
import type React from 'react';
import type { PropsWithChildren } from 'react';
import { AssignedAccountManagersWidget } from '@/components/shared/AssignedManagers';
import { DateValue } from '@/components/shared/DateValue';
import { useOverviewAccount } from '@/core/api/overview';
import { useAccountInfoSegments, useSelectedAccount } from '@/core/hooks';
import { UserCapabilities } from '@/core/types';
import { useCurrentUserCapabilityCheck } from '@/core/utils';

import { AccountStatus } from './AccountStatus';
import { DateEditor } from './DateEditor';
import { NextReviewDate } from './NextReviewDate';
import { ReviewStatus } from './ReviewStatus';
import { RiskProfile } from './RiskProfile';

const useStyles = createStyles(({ colors, radius }) => {
	const borderColor = colors.gray[3];

	return {
		wrapper: {
			paddingTop: '0.5rem',
			paddingBottom: '0.5rem',
			display: 'flex',
			borderRadius: radius.sm,
			minHeight: '4.7rem',

			'& > :not(:first-of-type)': {
				borderLeft: `1px solid ${borderColor}`,
			},
		},
		item: {
			paddingLeft: '0.75rem',
			paddingRight: '0.75rem',
			minWidth: '132px',
		},
		itemTitle: {
			color: colors.gray[5],
			fontWeight: 600,
			fontSize: '0.75rem',
			letterSpacing: '.5px',
		},
	};
});

const Item: React.FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => {
	const { classes } = useStyles();

	return (
		<Box className={classes.item}>
			<TFText pb="0.15rem" className={classes.itemTitle}>
				{label}
			</TFText>
			{children}
		</Box>
	);
};

export const AccountToolbar: React.FC = () => {
	const { classes } = useStyles();

	const canReviewAccounts = useCurrentUserCapabilityCheck(UserCapabilities.CAN_REVIEW_ACCOUNTS_DATA);
	const canEditAccounts = useCurrentUserCapabilityCheck(UserCapabilities.CAN_PREPARE_ACCOUNTS_DATA);

	const {
		accountStatus,
		onboardingDate,
		onboardingDateMutation,
		closingDate,
		clientRiskLevel,
		kycApprovalDate,
		kycApprovalDateMutation,
		nextKycReviewDate,
		nextKycReviewDateMutation,
	} = useAccountInfoSegments();

	const wasActivated = accountStatus && accountStatus !== 'PROSPECT' && accountStatus !== 'REJECTED';
	const isActive = accountStatus === 'ACTIVE' || accountStatus === 'SUSPENDED';
	const isClosed = accountStatus === 'CLOSED';
	const { meta } = useSelectedAccount();
	const accountId = meta.graphNodeId;
	const { data } = useOverviewAccount(accountId);
	const listUsers = data?.listUsers;

	const assignedManagers = data?.account.assignedManagers ?? [];

	return (
		<TFCard>
			<Box className={classes.wrapper}>
				<Item label="Account status">
					<AccountStatus />
				</Item>

				<Item label="Review status">
					<ReviewStatus />
				</Item>

				<Item label="Risk profile">
					<RiskProfile clientRiskLevel={clientRiskLevel} readonly={isClosed || !canReviewAccounts} />
				</Item>

				{wasActivated ? (
					<Item label="Onboarding date">
						{canEditAccounts ? (
							<DateEditor
								date={onboardingDate}
								isLoading={onboardingDateMutation.isPending}
								onSave={onboardingDateMutation.mutateAsync}
							/>
						) : (
							<DateValue value={onboardingDate} />
						)}
					</Item>
				) : null}

				{wasActivated ? (
					<Item label="Latest review">
						{isActive && canReviewAccounts ? (
							<DateEditor
								date={kycApprovalDate}
								isLoading={kycApprovalDateMutation.isPending}
								onSave={kycApprovalDateMutation.mutateAsync}
							/>
						) : (
							<DateValue value={kycApprovalDate} />
						)}
					</Item>
				) : null}

				{isActive ? (
					<Item label="Next review">
						{canReviewAccounts ? (
							<NextReviewDate
								baseDate={kycApprovalDate}
								date={nextKycReviewDate}
								isLoading={nextKycReviewDateMutation.isPending}
								onSave={nextKycReviewDateMutation.mutateAsync}
							/>
						) : (
							<DateValue
								value={nextKycReviewDate ? D.getDateDisplayText(nextKycReviewDate) : undefined}
								style={{
									color: nextKycReviewDate ? D.getDateDisplayColor(nextKycReviewDate) : undefined,
								}}
								raw={true}
							/>
						)}
					</Item>
				) : null}

				{isClosed ? (
					<Item label="Closing date">
						<DateValue value={closingDate} />
					</Item>
				) : null}

				<Item label="Assigned managers">
					<AssignedAccountManagersWidget
						accountId={accountId}
						selectedIds={assignedManagers}
						managers={listUsers ?? []}
					/>
				</Item>
			</Box>
		</TFCard>
	);
};
